import styled from "@emotion/styled/macro";
import { MOBILE_BREAKPOINT } from "@sunrun/experience-ui-components";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { OfferReviewBanners } from "components/organisms/OfferReviewBanners";
import { OfferReviewUtilityBillUploadBanner } from "components/molecules/OfferReviewUtilityBillUploadBanner";
import { OfferReviewSquareFootageEstimateBanner } from "components/molecules/OfferReviewSquareFootageEstimateBanner";
import { isBuilderOffer } from "utils/builderOffer";

type SimplifiedOfferReviewAlertBannersProps = {
  offer: ReviewOffer;
  preferredLanguage?: string;
  viewDocLoading?: boolean;
  onViewDocument: () => void;
  utilityBillPastDueBalanceFlowIsEnabled: boolean;
  utilityCompany?: string;
  utilityPastDueBalance?: number;
  showUtilityBillUploadBanner: boolean;
  showSquareFootageEstimateBanner: boolean;
  onUtilityBillClick: () => void;
};

const SimplifiedOfferReviewAlertBanners = ({
  offer,
  preferredLanguage,
  viewDocLoading,
  onViewDocument,
  utilityBillPastDueBalanceFlowIsEnabled,
  utilityCompany,
  utilityPastDueBalance,
  showUtilityBillUploadBanner,
  showSquareFootageEstimateBanner,
  onUtilityBillClick,
}: SimplifiedOfferReviewAlertBannersProps) => {
  const showUtilityBillBanner =
    showUtilityBillUploadBanner && Boolean(onUtilityBillClick);
  const showSquareFootageBanner =
    showSquareFootageEstimateBanner && Boolean(onUtilityBillClick);
  const showPastDueBalanceBanner =
    utilityBillPastDueBalanceFlowIsEnabled &&
    Boolean(utilityCompany) &&
    Boolean(utilityPastDueBalance);
  const showSpanishContractBanner =
    preferredLanguage === "Spanish" && !viewDocLoading;

  const hasVisibleAlerts =
    offer.isSigned ||
    isBuilderOffer(offer) ||
    showSpanishContractBanner ||
    showPastDueBalanceBanner ||
    showUtilityBillBanner ||
    showSquareFootageBanner;

  if (!hasVisibleAlerts) {
    return null;
  }

  return (
    <Stack data-testid="simplified-offer-review-alert-banners">
      <SharedBannersLayoutReset>
        <OfferReviewBanners
          offer={offer}
          preferredLanguage={preferredLanguage}
          viewDocLoading={viewDocLoading}
          onViewDocument={onViewDocument}
          utilityBillPastDueBalanceFlowIsEnabled={
            utilityBillPastDueBalanceFlowIsEnabled
          }
          utilityCompany={utilityCompany}
          utilityPastDueBalance={utilityPastDueBalance}
        />
      </SharedBannersLayoutReset>
      {showUtilityBillBanner && (
        <OfferReviewUtilityBillUploadBanner
          onClickPrimaryAction={onUtilityBillClick}
        />
      )}
      {showSquareFootageBanner && (
        <OfferReviewSquareFootageEstimateBanner
          homeSizeEstimate={offer.usage.homeSizeEstimate}
          onClickPrimaryAction={onUtilityBillClick}
        />
      )}
    </Stack>
  );
};

/** OfferReviewBanners adds standard review page padding; reset it inside the simplified stack. */
const SharedBannersLayoutReset = styled.div`
  & > div {
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0;
  }
`;

const Stack = styled.div`
  box-sizing: border-box;
  max-width: 1200px;
  width: calc(100% - 128px);
  margin: 0 auto 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: calc(100% - 32px);
  }
`;

export { SimplifiedOfferReviewAlertBanners };
